/**
 * `W` — draw a path (§4.3.1 `Path`). An **open** polyline, unlike the polygon
 * tool: `Enter` or a double-click finishes it, `Backspace` drops the last
 * point, `Esc` cancels. Two points are enough.
 */
import { angleLock } from '@/geo/snap'

import { idle, none, withState, type ToolHandler, type ToolResult } from './types'

export const MIN_PATH_POINTS = 2

export const pathTool: ToolHandler = {
  id: 'path',
  shortcut: 'w',
  icon: 'Route',
  label: 'Path',
  hint: 'Click along a route characters should follow. Press Enter when you are done.',
  cursor: 'crosshair',

  reduce(machine, ev, cfg): ToolResult | null {
    const st = machine.state
    const drawing = st.t === 'path.drawing' ? st : null

    if (ev.t === 'pointerdown' && ev.button === 0) {
      const at = drawing && ev.shift ? angleLock(drawing.points[drawing.points.length - 1] ?? ev.at, ev.at) : ev.at
      if (!drawing) return withState('path', { t: 'path.drawing', points: [at], cursor: at })
      return withState('path', { t: 'path.drawing', points: [...drawing.points, at], cursor: at })
    }

    if (ev.t === 'pointermove') {
      if (!drawing) return none(machine)
      const last = drawing.points[drawing.points.length - 1] ?? ev.at
      return withState('path', { ...drawing, cursor: ev.shift ? angleLock(last, ev.at) : ev.at })
    }

    if (ev.t === 'dblclick') {
      if (!drawing) return none(machine)
      return finish(drawing.points, cfg?.activeKind.path)
    }

    if (ev.t === 'key') {
      if (!drawing) return null
      if (ev.key === 'Enter') return finish(drawing.points, cfg?.activeKind.path)
      if (ev.key === 'Backspace') {
        const points = drawing.points.slice(0, -1)
        return points.length ? withState('path', { ...drawing, points }) : idle('path')
      }
      if (ev.key === 'Escape') return idle('path')
      return null
    }

    if (ev.t === 'cancel' || ev.t === 'pointercancel') return idle('path')

    return null
  },
}

function finish(points: readonly [number, number][], kind?: string): ToolResult {
  if (points.length < MIN_PATH_POINTS) return idle('path')
  return idle('path', [{ k: 'beginGesture' }, { k: 'createPath', points: points.map((p) => [p[0], p[1]]), ...(kind ? { kind } : {}) }, { k: 'endGesture' }])
}
